import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Search, Save, PhoneCall } from "lucide-react";

const STATUSES = [
  "New Request", "Consultation Scheduled", "Contacted", "Proposal Sent",
  "Payment Pending", "Service Started", "In Progress", "Completed", "Cancelled",
];

const STATUS_TONE = {
  "New Request":            "bg-slate-100 text-slate-700 border-slate-200",
  "Consultation Scheduled": "bg-amber-100 text-amber-800 border-amber-200",
  "Contacted":              "bg-sky-100 text-sky-800 border-sky-200",
  "Proposal Sent":          "bg-violet-100 text-violet-800 border-violet-200",
  "Payment Pending":        "bg-orange-100 text-orange-800 border-orange-200",
  "Service Started":        "bg-blue-100 text-blue-800 border-blue-200",
  "In Progress":            "bg-blue-100 text-blue-800 border-blue-200",
  "Completed":              "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Cancelled":              "bg-rose-100 text-rose-700 border-rose-200",
};

export default function AdminServiceRequests() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [smsNumber, setSmsNumber] = useState("");
  const [savingSms, setSavingSms] = useState(false);
  const [staff, setStaff] = useState([]);

  const load = async () => {
    try {
      const [reqResp, catsResp, smsResp, staffResp] = await Promise.all([
        api.get("/admin/service-requests", { params: { status: status || undefined, category: category || undefined, q: q || undefined } }),
        api.get("/services/categories"),
        api.get("/admin/services/settings/sms-number"),
        api.get("/admin/users"),
      ]);
      setItems(reqResp.data?.items || []);
      setCategories(catsResp.data?.categories || []);
      setSmsNumber(smsResp.data?.number || "");
      setStaff(staffResp.data?.users || staffResp.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category]);

  const saveSms = async () => {
    setSavingSms(true);
    try {
      await api.put("/admin/services/settings/sms-number", { number: smsNumber });
      toast.success("SMS notification number saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSavingSms(false); }
  };

  const patchItem = async (id, updates) => {
    try {
      const { data } = await api.put(`/admin/service-requests/${id}`, updates);
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const ql = q.toLowerCase();
    return items.filter((r) =>
      (r.client_name || "").toLowerCase().includes(ql) ||
      (r.client_email || "").toLowerCase().includes(ql) ||
      (r.services || []).some((s) => (s.name || "").toLowerCase().includes(ql))
    );
  }, [items, q]);

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Admin</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Service Requests</h1>
        <p className="text-slate-600 mt-1">Every consultation request from the client marketplace lands here.</p>

        {/* SMS Notification Setting */}
        <Card className="mt-6 p-4 rounded-2xl border-slate-100 flex items-center gap-3 flex-wrap" data-testid="services-sms-setting">
          <PhoneCall size={16} className="text-slate-500" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-700">SMS notification number</p>
            <p className="text-[11px] text-slate-500">Every new service request pings this number instantly via the Close CRM integration.</p>
          </div>
          <Input
            value={smsNumber}
            onChange={(e) => setSmsNumber(e.target.value)}
            className="max-w-[220px]"
            placeholder="+18605189218"
            data-testid="services-sms-number"
          />
          <Button onClick={saveSms} disabled={savingSms} className="bg-slate-900 text-white hover:bg-slate-800">
            <Save size={13} className="mr-1" /> Save
          </Button>
        </Card>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, email, service…" className="pl-9" data-testid="services-search-admin" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="filter-category">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="mt-6 space-y-4">
          {filtered.length === 0 && (
            <Card className="p-10 rounded-2xl border-slate-100 text-center text-slate-400 text-sm">No requests match those filters yet.</Card>
          )}
          {filtered.map((r) => (
            <RequestCard key={r.id} req={r} staff={staff} onPatch={patchItem} />
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function RequestCard({ req, staff, onPatch }) {
  const [notes, setNotes] = useState(req.internal_notes || "");
  const [saving, setSaving] = useState(false);
  const saveNotes = async () => {
    setSaving(true);
    await onPatch(req.id, { internal_notes: notes });
    setSaving(false);
  };
  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid={`svc-req-${req.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-widest font-black rounded-full border px-2 py-0.5 ${STATUS_TONE[req.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
              {req.status}
            </span>
            <span className="text-[11px] text-slate-400 font-semibold">
              Requested {new Date(req.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </span>
          </div>
          <p className="font-display font-black text-lg text-slate-900">{req.client_name} <span className="text-slate-400 font-semibold text-sm">· {req.client_email}</span></p>
          <div className="mt-2 text-sm text-slate-600 space-y-1">
            <p><b>Phone:</b> {req.client_phone || "—"}</p>
            <p><b>Services:</b> {(req.services || []).map((s) => s.name).join(", ")}</p>
            {req.preferred_slot_local && <p><b>Consultation:</b> {req.preferred_slot_local} <span className="text-slate-400">({req.timezone})</span></p>}
            {req.primary_goal && <p><b>Primary goal:</b> {req.primary_goal}</p>}
            {req.desired_launch_date && <p><b>Launch target:</b> {req.desired_launch_date}</p>}
          </div>
        </div>
        <div className="w-full sm:w-64 space-y-2">
          <label className="text-xs font-bold text-slate-500 block">
            Status
            <select
              value={req.status}
              onChange={(e) => onPatch(req.id, { status: e.target.value })}
              className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
              data-testid={`svc-status-${req.id}`}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold text-slate-500 block">
            Assigned to
            <select
              value={req.assigned_user_id || ""}
              onChange={(e) => onPatch(req.id, { assigned_user_id: e.target.value })}
              className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
              data-testid={`svc-assign-${req.id}`}
            >
              <option value="">Unassigned</option>
              {(staff || []).map((u) => <option key={u.id} value={u.id}>{u.first_name || u.name}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold text-slate-500 mb-1">Internal notes</p>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes only your team sees" data-testid={`svc-notes-${req.id}`} />
        <div className="mt-2 flex justify-end">
          <Button size="sm" onClick={saveNotes} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800">
            <Save size={12} className="mr-1" /> Save notes
          </Button>
        </div>
      </div>

      {Array.isArray(req.sms_notifications) && req.sms_notifications.length > 0 && (
        <p className="text-[10px] text-slate-400 mt-3">
          SMS sent {req.sms_notifications.length}× · last {req.sms_notifications[req.sms_notifications.length - 1].ok ? "delivered" : "failed"}
        </p>
      )}
    </Card>
  );
}
